/**
 * The `feature` layout of `DynamicProductTile` — CM Enhanced Content design
 * (Figma `CM-Enhanced-Content` node 93:1381): square photo well, underlined
 * title, then the two feature blocks. Badges, rating, price and Add to Cart
 * are the storefront's own sub-components behind the same toggles as the
 * standard tile. LMN / telehealth products never reach this card — the
 * organism routes them to their own tiles (SFCC-11677 / ET-225).
 */
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router";

import { ProductImage } from "~/components/product-list/product-image";
import { useSite, useURL } from "~/contexts";
import { emitProductClick } from "~/hec/analytics/event-layer/gtm-product-click-events";
import { ProductFeatureBlocks } from "~/hec/components/molecules/product-feature-blocks/product-feature-blocks";
import { ProductTilePrice } from "~/hec/components/product-tile/product-tile-price";
import { AddToCart } from "~/hec/components/product/add-to-cart";
import { OosOverlay } from "~/hec/components/product/oos-overlay";
import { ProductBadges } from "~/hec/components/product/product-badges";
import { Ratings } from "~/hec/components/product/ratings";
import {
  isTileProductInStock,
  tileCardView,
  tileClickItem,
} from "~/hec/dynamic-product-tile/dynamic-product-tile.logic";
import { getProductBadges } from "~/hec/types/product";
import { cn } from "~/lib/utils";
import { FavoritesCTA } from "~/routes/resources/favorites";
import { resolvePriceFromHecProduct } from "~/shop/utils/price-display";

import messages from "./messages";
import type { FeatureProductCardProps } from "./types";

export function FeatureProductCard({
  product,
  features,
  imageSrc,
  showBadges,
  showPrice,
  showRatings,
  showAddToCart,
  showFavorites,
  onOpenQuickView,
  borderColor,
  listId,
  listName,
  listIndex,
}: FeatureProductCardProps) {
  const url = useURL();
  const intl = useIntl();
  const { site } = useSite();
  const priceDisplay = useMemo(
    () => resolvePriceFromHecProduct(product),
    [product],
  );
  const { name, href, image } = tileCardView(product);
  const badges = showBadges ? getProductBadges(product) : [];
  // Single-tile use can show a sold-out product (the slider skips them):
  // grey it out and drop Quick View, as the standard tile does.
  const isSoldOut = !isTileProductInStock(product);
  const clickItem = tileClickItem(product, priceDisplay?.price);
  const onClick = () =>
    emitProductClick(clickItem, {
      listId,
      listName,
      index: listIndex,
      siteId: site.id,
    });

  return (
    <div
      className={cn(
        "group flex h-full w-full flex-col gap-[20px] bg-white p-[14px] sm:p-[20px]",
        borderColor && "border-2 border-solid",
      )}
      style={borderColor ? { borderColor } : undefined}
      data-testid={`product-tile-feature-card-${product.id}`}
    >
      {/* Square photo well with a 20px inset image box (catalog or authored
          photo alike). Badges + heart sit inside the box so they land on the
          image's corner, not the card's. */}
      <div className="aspect-square relative w-full overflow-hidden bg-white">
        <Link
          to={url(href)}
          prefetch="intent"
          state={{ tileProduct: product }}
          onClick={onClick}
          className="block size-full"
          // The title below is the accessible link to the same PDP; keep the
          // photo out of the tab order and the a11y tree so it isn't announced twice.
          tabIndex={-1}
          aria-hidden="true"
          data-testid={`product-tile-image-${product.id}`}
        >
          {imageSrc ? (
            // Builder-hosted asset: a plain <img>. `ProductImage` would add
            // SFCC image-service params to a URL that isn't SFCC's.
            <img
              src={imageSrc}
              alt={name}
              className="size-full object-cover"
              loading="lazy"
            />
          ) : image ? (
            <ProductImage image={image} type="plp" />
          ) : (
            <div className="flex size-full items-center justify-center text-sm text-gray-400">
              {intl.formatMessage(messages.noImage)}
            </div>
          )}
        </Link>
        <OosOverlay active={isSoldOut} />
        {badges.length > 0 && (
          <ProductBadges badges={badges} productId={product.id} />
        )}
        {showFavorites && (
          <FavoritesCTA
            productId={product.id}
            className="absolute right-2 top-2 z-10"
            iconHeight={22}
            iconWidth={22}
            noBorder
            wishlistGa4={{ ...clickItem, listId, listName, index: listIndex }}
          />
        )}
        {onOpenQuickView && !isSoldOut && (
          <button
            type="button"
            onClick={() => onOpenQuickView()}
            className="absolute bottom-0 left-0 z-10 flex w-full items-center justify-center bg-gray-02 py-1 opacity-100 transition-opacity lg:opacity-0 lg:focus-visible:opacity-100 lg:group-hover:opacity-100"
            data-testid={`product-tile-quick-view-${product.id}`}
          >
            <span className="font-grotesque text-[10px] font-medium uppercase tracking-wide text-blue-03">
              {intl.formatMessage(messages.quickView)}
            </span>
          </button>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[16px] sm:gap-[24px]">
        <div className="flex flex-col gap-2">
          {showRatings && (
            <Ratings
              rating={product.rating ?? 0}
              reviewCount={product.reviewCount}
              siteId={site.id}
              size="small"
            />
          )}
          {/* Two-line reserve (2 × 1.5 line-height) keeps the feature blocks aligned
              across a row. Mobile (<sm) type is 14px, 20px from sm up. */}
          <Link
            to={url(href)}
            prefetch="intent"
            state={{ tileProduct: product }}
            onClick={onClick}
            className="line-clamp-2 min-h-[42px] font-grotesque text-[14px] font-medium leading-[1.5] text-blue-04 underline sm:min-h-[60px] sm:text-[20px]"
            data-testid={`product-tile-name-${product.id}`}
          >
            {name}
          </Link>
          {showPrice && (
            <ProductTilePrice
              display={priceDisplay}
              data-testid={`product-tile-price-${product.id}`}
            />
          )}
          {showAddToCart && (
            <AddToCart
              product={product}
              isProductTile
              onOpenQuickView={onOpenQuickView}
            />
          )}
        </div>
        <ProductFeatureBlocks features={features} />
      </div>
    </div>
  );
}
